import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { procurementService } from '../services/api';
import { fmtDate, fmtDateTime, getDaysUntil, getOrgaoDisplay } from '../utils/formatters';
import { PORTAL_MAP } from './licitacoes/procurementUi';

// ==================== Constants ====================

const PIPELINE_STAGES = [
  { id: 'novas', label: 'Novas', statuses: ['novo', 'analisando', 'interessado'], gradient: 'from-green-500/20 to-green-500/5', border: 'border-green-500/30', text: 'text-green-400', dot: 'bg-green-400', headerBg: 'bg-green-500/10' },
  { id: 'participando', label: 'Participando', statuses: ['participando'], gradient: 'from-orange-500/20 to-orange-500/5', border: 'border-orange-500/30', text: 'text-orange-400', dot: 'bg-orange-400', headerBg: 'bg-orange-500/10' },
  { id: 'suspensa', label: 'Suspensa', statuses: ['suspensa'], gradient: 'from-amber-500/20 to-amber-500/5', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400', headerBg: 'bg-amber-500/10' },
  { id: 'encerradas', label: 'Encerradas', statuses: ['perdido', 'desistida', 'revogada'], gradient: 'from-red-500/20 to-red-500/5', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-400', headerBg: 'bg-red-500/10' },
];

const RESULT_BADGES: Record<string, { label: string; color: string }> = {
  ganho: { label: 'Ganho', color: 'bg-emerald-500/20 text-emerald-400' },
  perdido: { label: 'Perdido', color: 'bg-red-500/20 text-red-400' },
  desistida: { label: 'Desistida', color: 'bg-rose-500/20 text-rose-400' },
  revogada: { label: 'Revogada', color: 'bg-red-500/20 text-red-400' },
};

// PORTAL_MAP vem do procurementUi (fonte única, ~70 portais). Esta tela tinha a
// própria cópia parcial, e o efeito era o mesmo portal aparecer com badge aqui e
// slug cru na tela vizinha — dívida F-5 da auditoria de 13/08.

const fmt = (v: number | null | undefined) =>
  v != null
    ? 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const fmtCompact = (v: number | null | undefined) => {
  if (v == null) return '—';
  const n = Number(v);
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(0)}k`;
  return `R$ ${n.toFixed(0)}`;
};

// ==================== Types ====================

interface PipelineViewProps {
  onOpenDetail: (item: any) => void;
  onStatusChange: (itemId: number, newStatus: string) => Promise<void>;
  onRefresh: () => void;
  refreshKey?: number;
  // Filtros compartilhados com a TABELA (P2 auditoria 04/07): antes o Pipeline
  // puxava um universo próprio e alternar Tabela↔Pipeline mostrava conjuntos
  // diferentes sem aviso.
  filters?: { q?: string; searchId?: string };
}

// ==================== Component ====================

export default function PipelineView({ onOpenDetail, onStatusChange, onRefresh, refreshKey, filters }: PipelineViewProps) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragItem, setDragItem] = useState<any>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [timelineOffset, setTimelineOffset] = useState(0);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [automationFilter, setAutomationFilter] = useState<'all' | 'proposta' | 'monitorando' | 'disputa' | 'sem_acao'>('all');
  const timelineRef = useRef<HTMLDivElement>(null);

  // Load all non-descartado items
  const loadPipelineItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 500, statusNot: 'descartado', sortBy: 'dataEncerramento', sortOrder: 'ASC' };
      if (filters?.q) params.q = filters.q;
      if (filters?.searchId) params.searchId = filters.searchId;
      const result = await procurementService.getItems(params);
      const data = result?.data || (Array.isArray(result) ? result : []);
      setItems(data);
    } catch (e: any) {
      console.error('Erro ao carregar pipeline:', e);
    }
    setLoading(false);
  }, [filters?.q, filters?.searchId]);

  useEffect(() => { loadPipelineItems(); }, [loadPipelineItems, refreshKey]);

  // Separate won items
  const wonItems = useMemo(() =>
    items.filter(i => i.status === 'ganho').sort((a, b) => {
      const da = b.dataEncerramento ? new Date(b.dataEncerramento).getTime() : 0;
      const db = a.dataEncerramento ? new Date(a.dataEncerramento).getTime() : 0;
      return da - db;
    }),
  [items]);

  // Non-won items for pipeline
  const pipelineItems = useMemo(() => items.filter(i => i.status !== 'ganho'), [items]);

  // Automation counts (for filter badges, excludes won items)
  const automationCounts = useMemo(() => {
    let proposta = 0, monitorando = 0, disputa = 0, semAcao = 0;
    pipelineItems.forEach(item => {
      const hasP = item._proposalCount > 0;
      const hasM = item.monitored;
      const hasD = item._disputeCount > 0;
      if (hasP) proposta++;
      if (hasM) monitorando++;
      if (hasD) disputa++;
      if (!hasP && !hasM && !hasD) semAcao++;
    });
    return { proposta, monitorando, disputa, semAcao };
  }, [pipelineItems]);

  // Filter items by automation state
  const filteredItems = useMemo(() => {
    if (automationFilter === 'all') return pipelineItems;
    return pipelineItems.filter(item => {
      if (automationFilter === 'proposta') return item._proposalCount > 0;
      if (automationFilter === 'monitorando') return item.monitored;
      if (automationFilter === 'disputa') return item._disputeCount > 0;
      if (automationFilter === 'sem_acao') return !item._proposalCount && !item.monitored && !item._disputeCount;
      return true;
    });
  }, [pipelineItems, automationFilter]);

  // Group items by pipeline stage
  const stageItems = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    PIPELINE_STAGES.forEach(s => { grouped[s.id] = []; });
    filteredItems.forEach(item => {
      const stage = PIPELINE_STAGES.find(s => s.statuses.includes(item.status));
      if (stage) grouped[stage.id].push(item);
    });
    // Sort each column: future encerramentos first (soonest on top), then past (most recent on top)
    const now = Date.now();
    Object.values(grouped).forEach(arr => {
      arr.sort((a, b) => {
        const da = a.dataEncerramento ? new Date(a.dataEncerramento).getTime() : Infinity;
        const db = b.dataEncerramento ? new Date(b.dataEncerramento).getTime() : Infinity;
        const aFuture = da >= now;
        const bFuture = db >= now;
        // Future items come before past items
        if (aFuture && !bFuture) return -1;
        if (!aFuture && bFuture) return 1;
        // Both future: soonest first (ASC)
        if (aFuture && bFuture) return da - db;
        // Both past: most recent first (DESC)
        return db - da;
      });
    });
    return grouped;
  }, [filteredItems]);

  // Timeline: build 21 days from today
  const timelineDays = useMemo(() => {
    const days: { date: Date; key: string; label: string; dayNum: number; weekday: string; isToday: boolean; isWeekend: boolean; items: any[] }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = -3; i < 18; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
      days.push({
        date: d,
        key,
        label: `${d.getDate()}/${d.getMonth() + 1}`,
        dayNum: d.getDate(),
        weekday: weekdays[d.getDay()],
        isToday: i === 0,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        items: [],
      });
    }
    // Map items to days by encerramento date
    items.forEach(item => {
      if (!item.dataEncerramento) return;
      const enc = item.dataEncerramento.split('T')[0];
      const day = days.find(d => d.key === enc);
      if (day) day.items.push(item);
    });
    return days;
  }, [items]);

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, item: any) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id.toString());
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!dragItem) return;

    const stage = PIPELINE_STAGES.find(s => s.id === stageId);
    if (!stage) return;

    // If the item is already in this stage, do nothing
    if (stage.statuses.includes(dragItem.status)) {
      setDragItem(null);
      return;
    }

    // Use the first status of the target stage
    const newStatus = stage.statuses[0];
    try {
      await onStatusChange(dragItem.id, newStatus);
      // Update local state optimistically
      setItems(prev => prev.map(i => i.id === dragItem.id ? { ...i, status: newStatus } : i));
    } catch (e) {
      console.error('Erro ao mudar status:', e);
    }
    setDragItem(null);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverStage(null);
  };

  // Urgency helpers
  const getUrgencyColor = (item: any) => {
    const days = getDaysUntil(item.dataEncerramento);
    if (days === null) return 'border-l-slate-600';
    if (days < 0) return 'border-l-slate-600';
    if (days <= 1) return 'border-l-red-500';
    if (days <= 3) return 'border-l-orange-500';
    if (days <= 7) return 'border-l-yellow-500';
    return 'border-l-slate-600';
  };

  const getTimelineDotColor = (item: any) => {
    if (['participando'].includes(item.status)) return 'bg-orange-400';
    if (['analisando', 'interessado'].includes(item.status)) return 'bg-yellow-400';
    if (['ganho'].includes(item.status)) return 'bg-emerald-400';
    if (['perdido', 'revogada'].includes(item.status)) return 'bg-red-400';
    const days = getDaysUntil(item.dataEncerramento);
    if (days !== null && days <= 1) return 'bg-red-400';
    if (days !== null && days <= 3) return 'bg-orange-400';
    return 'bg-green-400';
  };

  const getCountdownText = (item: any) => {
    const days = getDaysUntil(item.dataEncerramento);
    if (days === null) return '';
    if (days < 0) return `${Math.abs(days)}d atras`;
    if (days === 0) return 'Hoje';
    if (days === 1) return 'Amanha';
    return `em ${days}d`;
  };

  // ==================== Render ====================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-slate-400">Carregando pipeline...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Timeline Strip */}
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-slate-300">Encerramentos Proximos</h3>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span> &lt;24h</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block"></span> &lt;3d</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span> &lt;7d</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block"></span> &gt;7d</span>
          </div>
        </div>
        <div className="relative overflow-x-auto" ref={timelineRef}>
          <div className="flex gap-0 min-w-max">
            {timelineDays.map((day) => (
              <div
                key={day.key}
                className={`flex flex-col items-center px-2 py-2 min-w-[60px] border-r border-slate-700/50 relative cursor-default transition-colors
                  ${day.isToday ? 'bg-orange-500/10 border-b-2 border-b-orange-500' : ''}
                  ${day.isWeekend ? 'bg-slate-800/80' : ''}
                  ${hoveredDay === day.key ? 'bg-slate-700/50' : ''}
                `}
                onMouseEnter={(e) => {
                  if (day.items.length > 0) {
                    setHoveredDay(day.key);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.bottom + 8 });
                  }
                }}
                onMouseLeave={() => { setHoveredDay(null); setTooltipPos(null); }}
              >
                <span className={`text-[10px] ${day.isToday ? 'text-orange-400 font-bold' : 'text-slate-500'}`}>
                  {day.weekday}
                </span>
                <span className={`text-sm font-medium ${day.isToday ? 'text-orange-400' : 'text-slate-300'}`}>
                  {day.dayNum}
                </span>
                {/* Dots */}
                <div className="flex flex-wrap justify-center gap-0.5 mt-1.5 min-h-[12px]">
                  {day.items.slice(0, 6).map((item, idx) => (
                    <span
                      key={item.id}
                      className={`w-2.5 h-2.5 rounded-full ${getTimelineDotColor(item)} cursor-pointer hover:scale-150 transition-transform`}
                      title={`${getOrgaoDisplay(item)} - ${fmtCompact(item.valorEstimado)}`}
                      onClick={() => onOpenDetail(item)}
                    />
                  ))}
                  {day.items.length > 6 && (
                    <span className="text-[9px] text-slate-400">+{day.items.length - 6}</span>
                  )}
                </div>
                {day.items.length > 0 && (
                  <span className="text-[9px] text-slate-400 mt-0.5">{day.items.length}</span>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Tooltip */}
        {hoveredDay && tooltipPos && (() => {
          const day = timelineDays.find(d => d.key === hoveredDay);
          if (!day || day.items.length === 0) return null;
          return (
            <div
              className="fixed z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 max-w-xs"
              style={{ left: tooltipPos.x, top: tooltipPos.y, transform: 'translateX(-50%)' }}
            >
              <div className="text-xs font-medium text-slate-300 mb-2">{fmtDate(day.key)} - {day.items.length} licitacao(oes)</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {day.items.map(item => (
                  <div
                    key={item.id}
                    className="flex items-start gap-2 text-xs cursor-pointer hover:bg-slate-700/50 rounded p-1 -mx-1"
                    onClick={() => { onOpenDetail(item); setHoveredDay(null); }}
                  >
                    <span className={`w-2 h-2 rounded-full mt-0.5 flex-shrink-0 ${getTimelineDotColor(item)}`}></span>
                    <div className="min-w-0">
                      <div className="text-slate-200 truncate">{getOrgaoDisplay(item)}</div>
                      <div className="text-slate-400">{fmtCompact(item.valorEstimado)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Automation Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-400 mr-1">Filtrar por:</span>
        {([
          { key: 'all' as const, label: 'Todos', count: pipelineItems.length, icon: null, color: 'text-slate-300', bg: 'bg-slate-600/50 border-slate-500/50', activeBg: 'bg-slate-600 border-slate-400' },
          { key: 'proposta' as const, label: 'Com Proposta', count: automationCounts.proposta,
            icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
            color: 'text-blue-300', bg: 'bg-blue-500/10 border-blue-500/30', activeBg: 'bg-blue-500/20 border-blue-400' },
          { key: 'monitorando' as const, label: 'Monitorando', count: automationCounts.monitorando,
            icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
            color: 'text-green-300', bg: 'bg-green-500/10 border-green-500/30', activeBg: 'bg-green-500/20 border-green-400' },
          { key: 'disputa' as const, label: 'Robo de Lances', count: automationCounts.disputa,
            icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /><circle cx="12" cy="12" r="6" strokeWidth={2} /><circle cx="12" cy="12" r="2" strokeWidth={2} /></svg>,
            color: 'text-cyan-300', bg: 'bg-cyan-500/10 border-cyan-500/30', activeBg: 'bg-cyan-500/20 border-cyan-400' },
          { key: 'sem_acao' as const, label: 'Sem Acao', count: automationCounts.semAcao,
            icon: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
            color: 'text-slate-400', bg: 'bg-slate-600/10 border-slate-600/30', activeBg: 'bg-slate-600/30 border-slate-500' },
        ] as const).map(f => (
          <button
            key={f.key}
            onClick={() => setAutomationFilter(automationFilter === f.key ? 'all' : f.key)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              automationFilter === f.key ? `${f.activeBg} ${f.color}` : `${f.bg} ${f.color} opacity-70 hover:opacity-100`
            }`}
          >
            {f.icon}
            {f.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${automationFilter === f.key ? 'bg-white/10' : 'bg-white/5'}`}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-4 gap-3" style={{ minHeight: '500px' }}>
        {PIPELINE_STAGES.map((stage) => {
          const stageItemsList = stageItems[stage.id] || [];
          const isOver = dragOverStage === stage.id;
          const totalValue = stageItemsList.reduce((sum: number, item: any) => sum + (item.valorEstimado || 0), 0);

          return (
            <div
              key={stage.id}
              className={`flex flex-col rounded-lg border ${stage.border} bg-gradient-to-b ${stage.gradient} transition-all ${
                isOver ? 'ring-2 ring-orange-500 scale-[1.01]' : ''
              }`}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
            >
              {/* Column Header */}
              <div className={`${stage.headerBg} rounded-t-lg px-3 py-2.5 border-b ${stage.border}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${stage.dot}`}></span>
                    <span className={`text-sm font-semibold ${stage.text}`}>{stage.label}</span>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stage.headerBg} ${stage.text}`}>
                    {stageItemsList.length}
                  </span>
                </div>
                {totalValue > 0 && (
                  <div className="text-[10px] text-slate-400 mt-1">{fmtCompact(totalValue)} total</div>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[400px] max-h-[600px]">
                {stageItemsList.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-32 text-slate-500 text-xs">
                    <svg className="w-6 h-6 mb-1 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    {dragItem ? 'Solte aqui' : 'Nenhum item'}
                  </div>
                )}
                {stageItemsList.map((item) => {
                  const countdown = getCountdownText(item);
                  const daysLeft = getDaysUntil(item.dataEncerramento);
                  const portalInfo = PORTAL_MAP[item.portalOrigem] || { label: item.portalOrigem || '—', color: 'text-slate-400' };
                  const resultBadge = RESULT_BADGES[item.status];

                  return (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragEnd={handleDragEnd}
                      onClick={() => onOpenDetail(item)}
                      className={`bg-slate-800/80 rounded-lg border border-slate-700/50 p-2.5 cursor-pointer
                        hover:border-slate-500 hover:bg-slate-700/80 transition-all
                        border-l-[3px] ${getUrgencyColor(item)}
                        ${dragItem?.id === item.id ? 'opacity-40 scale-95' : ''}
                        active:cursor-grabbing
                      `}
                    >
                      {/* Top row: portal + countdown */}
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-[10px] ${portalInfo.color}`}>{portalInfo.label}</span>
                        {countdown && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            daysLeft !== null && daysLeft <= 1 ? 'bg-red-500/20 text-red-400' :
                            daysLeft !== null && daysLeft <= 3 ? 'bg-orange-500/20 text-orange-400' :
                            daysLeft !== null && daysLeft <= 7 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-slate-600/30 text-slate-400'
                          }`}>
                            {countdown}
                          </span>
                        )}
                      </div>

                      {/* Orgao name */}
                      <div className="text-xs font-medium text-slate-200 truncate mb-1" title={getOrgaoDisplay(item)}>
                        {getOrgaoDisplay(item)}
                      </div>

                      {/* Object */}
                      <div className="text-[10px] text-slate-400 line-clamp-2 mb-2 leading-tight" title={item.objetoCompra}>
                        {item.objetoCompra}
                      </div>

                      {/* Value + date row */}
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-slate-300">{fmtCompact(item.valorEstimado)}</span>
                        <div className="flex items-center gap-1">
                          {item.uf && <span className="text-[9px] px-1 py-0.5 rounded bg-slate-600/30 text-slate-400">{item.uf}</span>}
                          {item.srp && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400">SRP</span>}
                          {resultBadge && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${resultBadge.color}`}>
                              {resultBadge.label}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Encerramento date */}
                      {item.dataEncerramento && (
                        <div className="text-[10px] text-slate-500 mb-2 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {fmtDateTime(item.dataEncerramento)}
                        </div>
                      )}

                      {/* Automation status bar - clear visual indicators */}
                      {(item._proposalCount > 0 || item.monitored || item._disputeCount > 0) ? (
                        <div className="flex flex-wrap gap-1 pt-1.5 border-t border-slate-700/50">
                          {item._proposalCount > 0 && (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md font-medium ${
                              item._proposalStatus === 'enviada' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                              : item._proposalStatus === 'aceita' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                              : 'bg-slate-600/40 text-slate-300 border border-slate-600/50'
                            }`}>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {item._proposalStatus === 'enviada' ? 'Proposta Enviada'
                                : item._proposalStatus === 'aceita' ? 'Proposta Aceita'
                                : 'Proposta Rascunho'}
                            </span>
                          )}
                          {item.monitored && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Monitorando
                            </span>
                          )}
                          {item._disputeCount > 0 && (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md font-medium ${
                              item._disputeStatus === 'ativo' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                              : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                            }`}>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth={2} />
                                <circle cx="12" cy="12" r="6" strokeWidth={2} />
                                <circle cx="12" cy="12" r="2" strokeWidth={2} />
                              </svg>
                              {item._disputeStatus === 'ativo' ? 'Robo Ativo' : 'Disputa'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="pt-1.5 border-t border-slate-700/50">
                          <span className="text-[10px] text-slate-500 italic">Sem automacao</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Ganhos Section */}
      {wonItems.length > 0 && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-semibold text-emerald-400">Licitacoes Ganhas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold">{wonItems.length}</span>
            </div>
            <div className="text-xs text-emerald-400/70">
              Total estimado: {fmt(wonItems.reduce((sum: number, i: any) => sum + (i.valorEstimado || 0), 0))}
            </div>
          </div>
          <div className="divide-y divide-emerald-500/10">
            {wonItems.map((item) => {
              const portalInfo = PORTAL_MAP[item.portalOrigem] || { label: item.portalOrigem || '—', color: 'text-slate-400' };
              return (
                <div
                  key={item.id}
                  onClick={() => onOpenDetail(item)}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-emerald-500/5 cursor-pointer transition-colors"
                >
                  {/* Green check icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-slate-200 truncate">{getOrgaoDisplay(item)}</span>
                      {item.uf && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600/30 text-slate-400 flex-shrink-0">{item.uf}</span>}
                    </div>
                    <div className="text-xs text-slate-400 truncate">{item.objetoCompra}</div>
                  </div>

                  {/* Portal */}
                  <div className={`text-xs flex-shrink-0 ${portalInfo.color}`}>{portalInfo.label}</div>

                  {/* Value */}
                  <div className="flex-shrink-0 text-right">
                    <div className="text-sm font-medium text-emerald-400">{fmtCompact(item.valorEstimado)}</div>
                    {item.dataEncerramento && (
                      <div className="text-[10px] text-slate-500">{fmtDate(item.dataEncerramento)}</div>
                    )}
                  </div>

                  {/* Summary badges */}
                  <div className="flex-shrink-0 flex flex-col gap-1 min-w-[120px]">
                    {item._proposalCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-blue-500/15 text-blue-300">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Proposta {item._proposalStatus || 'cadastrada'}
                      </span>
                    )}
                    {item._disputeCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-cyan-500/15 text-cyan-300">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" strokeWidth={2} /><circle cx="12" cy="12" r="6" strokeWidth={2} /><circle cx="12" cy="12" r="2" strokeWidth={2} />
                        </svg>
                        Robo utilizado
                      </span>
                    )}
                    {!item._proposalCount && !item._disputeCount && (
                      <span className="text-[10px] text-slate-500 italic">Manual</span>
                    )}
                  </div>

                  {/* Arrow */}
                  <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary footer */}
      <div className="flex items-center justify-between text-xs text-slate-500 px-1">
        <span>{pipelineItems.length} no pipeline + {wonItems.length} ganhas (excl. descartadas)</span>
        <button
          onClick={() => { loadPipelineItems(); onRefresh(); }}
          className="flex items-center gap-1 text-slate-400 hover:text-slate-200 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Atualizar
        </button>
      </div>
    </div>
  );
}
